import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Refresh,
  Email,
  CheckCircle,
  TrendingUp,
  Search,
  Visibility,
} from '@mui/icons-material';
import { getUserReferrals, getMyTrackingStats } from './referralUtils';
import { formatDate } from './referralUtils';
import { MESSAGES } from './referralConfig';

const SUBSCRIPTION_STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const PERIOD_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 0, label: 'All time' },
];

const PAGE_SIZE = 30;

const ReferralsTracking = ({ isChampions = false }) => {
  const [referrals, setReferrals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [trackingStats, setTrackingStats] = useState({ unique_clicks: 0, registrations: 0, conversion_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodDays, setPeriodDays] = useState(30);
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  const loadData = async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);
      const days = periodDays || 0;
      const [refData, statsData] = await Promise.all([
        getUserReferrals(days, pageNum + 1, PAGE_SIZE),
        getMyTrackingStats(days),
      ]);
      const list = refData.referrals || [];
      setReferrals(list);
      const total = refData.total_count ?? refData.totalCount ?? list.length;
      setTotalCount(total >= list.length ? total : list.length);
      setTrackingStats({
        unique_clicks: statsData.unique_clicks ?? 0,
        registrations: statsData.registrations ?? 0,
        conversion_rate: statsData.conversion_rate ?? 0,
      });
    } catch (err) {
      setError('Failed to load referrals');
      console.error('Error loading referrals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [periodDays]);

  useEffect(() => {
    loadData(page);
  }, [periodDays, page]);

  const loadReferrals = () => loadData(page);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const filteredReferrals = useMemo(() => {
    return referrals.filter((r) => {
      const nameMatch = !filterName || (r.name || '').toLowerCase().includes(filterName.toLowerCase());
      const emailMatch = isChampions || !filterEmail || (r.email || r.referred_email || '').toLowerCase().includes(filterEmail.toLowerCase());
      const statusMatch = filterStatus === 'All' || (r.subscription_status || '') === filterStatus;
      return nameMatch && emailMatch && statusMatch;
    });
  }, [referrals, filterName, filterEmail, filterStatus, isChampions]);

  const nameOptions = useMemo(() => [...new Set(referrals.map((r) => r.name).filter(Boolean))], [referrals]);
  const emailOptions = useMemo(() => [...new Set(referrals.map((r) => r.email || r.referred_email).filter(Boolean))], [referrals]);

  const getStatusChip = (referral) => {
    const status = referral.subscription_status || 'Inactive';
    return (
      <Chip
        label={status}
        color={status === 'Active' ? 'success' : 'default'}
        size="small"
        icon={status === 'Active' ? <CheckCircle /> : null}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Referrals Tracking
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={loadReferrals}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {/* Timeline filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Time period</InputLabel>
          <Select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            label="Time period"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Stats Cards: Total Referrals, Unique Clicks, Registrations, Conversion */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {referrals.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total interactions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Visibility sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {trackingStats.unique_clicks.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Clicks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {trackingStats.registrations.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registrations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {trackingStats.conversion_rate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conversion Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadReferrals}>
            Retry
          </Button>
        } sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Referrals Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Referral History ({filteredReferrals.length} of {totalCount})
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={isChampions ? 6 : 4}>
              <Autocomplete
                freeSolo
                options={nameOptions}
                value={filterName}
                onInputChange={(_, v) => setFilterName(v || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Name"
                    size="small"
                    placeholder="Search by name"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            {!isChampions && (
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={emailOptions}
                  value={filterEmail}
                  onInputChange={(_, v) => setFilterEmail(v || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Email ID"
                      size="small"
                      placeholder="Search by email"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={isChampions ? 6 : 4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  {SUBSCRIPTION_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {totalCount === 0 && !loading ? (
            <Alert severity="info">
              {MESSAGES.INFO.NO_REFERRALS}
            </Alert>
          ) : filteredReferrals.length === 0 ? (
            <Alert severity="info">No referrals match the current filters on this page.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      {!isChampions && <TableCell>Email ID</TableCell>}
                      <TableCell>Subscription Status</TableCell>
                      <TableCell>Registered on</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReferrals.map((referral) => (
                      <TableRow key={referral.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {referral.name || '—'}
                          </Typography>
                        </TableCell>
                        {!isChampions && (
                          <TableCell>
                            <Typography variant="body2">
                              {referral.email || referral.referred_email || '—'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          {getStatusChip(referral)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {referral.registered_on ? formatDate(referral.registered_on) : (referral.registered_at ? formatDate(referral.registered_at) : '—')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {referral.total_amount != null && referral.total_amount !== undefined
                              ? `$${Number(referral.total_amount).toFixed(2)}`
                              : '$0.00'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Empty State with Tips */}
      {totalCount === 0 && referrals.length === 0 && !loading && !error && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Referrals Yet?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Here are some tips to get started with referrals:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Share your referral link on social media
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Send personalized invites to friends and family
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Include your referral link in your email signature
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Mention it in professional networks and communities
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ReferralsTracking;
